<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import APIHelper from 'dashboard/helper/APIHelper';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const authenticatedAxios = APIHelper(window.axios);
const currentAccountId = useMapGetter('getCurrentAccountId');
const apiBase = computed(
  () => `/api/v1/accounts/${currentAccountId.value}/bonus_code_batches`
);

const isLoading = ref(false);
const batches = ref([]);
const showUploadModal = ref(false);
const isUploading = ref(false);
const deleteDialogRef = ref(null);
const selectedBatchId = ref(null);

const form = ref({ name: '', default_expiry_at: '', file: null });

const loadBatches = async () => {
  try {
    isLoading.value = true;
    const response = await authenticatedAxios.get(apiBase.value);
    batches.value = response.data.payload || [];
  } catch (error) {
    useAlert(t('BONUS_CODES.TOAST.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onFileChange = event => {
  form.value.file = event.target.files?.[0] || null;
};

const openUploadModal = () => {
  form.value = { name: '', default_expiry_at: '', file: null };
  showUploadModal.value = true;
};

const closeUploadModal = () => {
  showUploadModal.value = false;
};

const uploadBatch = async () => {
  if (!form.value.file) {
    useAlert(t('BONUS_CODES.UPLOAD.FILE_REQUIRED'));
    return;
  }
  try {
    isUploading.value = true;
    const fd = new FormData();
    fd.append('file', form.value.file);
    if (form.value.name) fd.append('name', form.value.name);
    if (form.value.default_expiry_at)
      fd.append('default_expiry_at', form.value.default_expiry_at);

    const response = await authenticatedAxios.post(apiBase.value, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const imported = response.data.import?.imported ?? 0;
    useAlert(t('BONUS_CODES.TOAST.UPLOAD_SUCCESS', { count: imported }));
    closeUploadModal();
    await loadBatches();
  } catch (error) {
    const msg = error.response?.data?.error || t('BONUS_CODES.TOAST.UPLOAD_ERROR');
    useAlert(msg);
  } finally {
    isUploading.value = false;
  }
};

const openDeleteDialog = id => {
  selectedBatchId.value = id;
  deleteDialogRef.value?.open();
};

const deleteBatch = async () => {
  if (!selectedBatchId.value) return;
  try {
    await authenticatedAxios.delete(`${apiBase.value}/${selectedBatchId.value}`);
    useAlert(t('BONUS_CODES.TOAST.DELETE_SUCCESS'));
    await loadBatches();
  } catch (error) {
    useAlert(t('BONUS_CODES.TOAST.DELETE_ERROR'));
  } finally {
    deleteDialogRef.value?.close();
  }
};

const formatDate = d => (d ? new Date(d).toLocaleString() : '—');

onMounted(loadBatches);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div class="sticky top-0 z-20 bg-n-background border-b border-n-container">
      <div class="px-6 py-4 flex items-center justify-between">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('BONUS_CODES.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('BONUS_CODES.DESCRIPTION') }}
          </p>
        </div>
        <Button
          icon="i-lucide-upload"
          :label="t('BONUS_CODES.UPLOAD_BUTTON')"
          @click="openUploadModal"
        />
      </div>
    </div>

    <div class="flex-1 overflow-auto p-6">
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <woot-loading-state :message="t('BONUS_CODES.LOADING')" />
      </div>

      <div
        v-else-if="!batches.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <fluent-icon icon="ticket" class="text-n-slate-11 mb-4" size="32" />
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ t('BONUS_CODES.EMPTY_TITLE') }}
        </h3>
        <p class="text-n-slate-11 mb-6">{{ t('BONUS_CODES.EMPTY_SUBTITLE') }}</p>
        <Button
          icon="i-lucide-upload"
          :label="t('BONUS_CODES.UPLOAD_BUTTON')"
          @click="openUploadModal"
        />
      </div>

      <div
        v-else
        class="bg-n-solid-2 rounded-xl border border-n-container overflow-hidden"
      >
        <table class="min-w-full text-sm">
          <thead class="bg-n-slate-2">
            <tr>
              <th class="px-4 py-3 text-left font-medium text-n-slate-12">
                {{ t('BONUS_CODES.COL.NAME') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-12">
                {{ t('BONUS_CODES.COL.TOTAL') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-emerald-11">
                {{ t('BONUS_CODES.COL.AVAILABLE') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-blue-11">
                {{ t('BONUS_CODES.COL.ASSIGNED') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-amber-11">
                {{ t('BONUS_CODES.COL.EXPIRED') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-12">
                {{ t('BONUS_CODES.COL.DEFAULT_EXPIRY') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-12">
                {{ t('BONUS_CODES.COL.CREATED') }}
              </th>
              <th class="px-4 py-3" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr v-for="b in batches" :key="b.id" class="hover:bg-n-slate-1">
              <td class="px-4 py-3 font-medium text-n-slate-12">{{ b.name }}</td>
              <td class="px-4 py-3 text-n-slate-12">{{ b.total_count }}</td>
              <td class="px-4 py-3 text-n-emerald-11 font-medium">
                {{ b.available_count }}
              </td>
              <td class="px-4 py-3 text-n-blue-11">{{ b.assigned_count }}</td>
              <td class="px-4 py-3 text-n-amber-11">{{ b.expired_count }}</td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ formatDate(b.default_expiry_at) }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ formatDate(b.created_at) }}
              </td>
              <td class="px-4 py-3 text-right">
                <Button
                  icon="i-lucide-trash-2"
                  ruby
                  xs
                  faded
                  @click="openDeleteDialog(b.id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <woot-modal v-model:show="showUploadModal" :on-close="closeUploadModal">
    <div class="p-6">
      <h2 class="text-xl font-semibold text-n-slate-12 mb-1">
        {{ t('BONUS_CODES.UPLOAD.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11 mb-6">
        {{ t('BONUS_CODES.UPLOAD.SUBTITLE') }}
      </p>

      <div class="flex flex-col gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('BONUS_CODES.UPLOAD.NAME_LABEL') }}
          </label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-4 py-2.5 border border-n-container rounded-lg bg-n-solid-2 text-n-slate-12"
            :placeholder="t('BONUS_CODES.UPLOAD.NAME_PLACEHOLDER')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('BONUS_CODES.UPLOAD.EXPIRY_LABEL') }}
          </label>
          <input
            v-model="form.default_expiry_at"
            type="datetime-local"
            class="w-full px-4 py-2.5 border border-n-container rounded-lg bg-n-solid-2 text-n-slate-12"
          />
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('BONUS_CODES.UPLOAD.EXPIRY_HELP') }}
          </p>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('BONUS_CODES.UPLOAD.FILE_LABEL') }}
          </label>
          <input
            type="file"
            accept=".csv,text/csv"
            class="w-full text-sm text-n-slate-11"
            @change="onFileChange"
          />
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('BONUS_CODES.UPLOAD.FILE_HELP') }}
          </p>
        </div>
      </div>

      <div
        class="flex justify-end gap-2 mt-6 pt-6 border-t border-n-container"
      >
        <Button
          slate
          :label="t('BONUS_CODES.UPLOAD.CANCEL')"
          @click="closeUploadModal"
        />
        <Button
          blue
          :label="t('BONUS_CODES.UPLOAD.SUBMIT')"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="uploadBatch"
        />
      </div>
    </div>
  </woot-modal>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('BONUS_CODES.DELETE.TITLE')"
    :description="t('BONUS_CODES.DELETE.DESCRIPTION')"
    :confirm-button-label="t('BONUS_CODES.DELETE.CONFIRM')"
    :cancel-button-label="t('BONUS_CODES.DELETE.CANCEL')"
    @confirm="deleteBatch"
  />
</template>
